// Shared API helper for talking to the Hiilt gateway (user-service auth + manasa-service).
// Every manasa-service response is an ApiResponse envelope ({success, payload, error}).

import { applyBranding } from "./Branding";
import { previewRequest, listenForDraft } from "./Theme";

const properties = require("../properties.json");

const TOKEN_KEY = "manasa_student_token";
const USER_KEY = "manasa_student_user";

export function getToken() {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function getCurrentUser() {
  const raw = window.localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function isLoggedIn() {
  return !!getToken();
}

export function saveSession(token, user) {
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession() {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
}

function buildQuery(query) {
  if (!query) return "";
  const parts = Object.entries(query)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

/** Calls manasa-service through the gateway. `path` is relative to properties.api_path. */
export async function apiFetch(path, { method = "GET", body, query, auth = false } = {}) {
  const url = properties.api_path + path + buildQuery(query);
  const headers = { Accept: "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return null;

  const json = await res.json();
  if (!json.success) {
    throw new Error((json.error && json.error.message) || "حدث خطأ غير متوقع");
  }
  return json.payload;
}

/** Resolves which teacher the current hostname belongs to. Accepts an explicit `host` for
 *  local testing (?host=example.com) -- otherwise uses the real browser hostname.
 *
 *  On localhost the override is remembered for the tab, because react-router drops the query
 *  string on every in-app <Link>: without this, ?host= resolves the page you typed it on and
 *  then every link off it falls back to "localhost", which matches no tenant, so the whole site
 *  reads as "no page was found for this domain" the moment you click anything.
 *
 *  Deliberately localhost-only, so a deployed site behaves exactly as before -- it reads the
 *  real hostname and never consults, or writes, this storage. */
function isLocalHost(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

export function currentHost() {
  const params = new URLSearchParams(window.location.search);
  const override = params.get("host");
  const hostname = window.location.hostname;

  if (!isLocalHost(hostname)) return override || hostname;

  // sessionStorage can throw (private mode, blocked site data), and a dev
  // convenience must never be the thing that breaks the page.
  if (override) {
    try { window.sessionStorage.setItem("devHost", override); } catch (e) { /* ignore */ }
    return override;
  }
  try {
    const remembered = window.sessionStorage.getItem("devHost");
    if (remembered) return remembered;
  } catch (e) { /* ignore */ }

  return hostname;
}

/* The tenant is a property of the host, so it cannot change while the tab is
 * open. Resolving it once and keeping it is what makes moving between pages
 * feel like one app: every page used to request it again on mount and render
 * nothing until it came back, which blanked the whole screen -- header and
 * footer included -- on every single navigation.
 *
 * The in-flight promise is shared too, so two pages mounting at once make one
 * request rather than two. A failure clears it, so a retry is still possible. */
let tenantCache = null;
let tenantPromise = null;

/** Local development only: a theme written in the backend's resources/themes but not yet on the server it talks to
 *  (setupProxy serves those files at /__dev-themes). Anywhere else the server's answer stands. */
function devTheme(id, err) {
  if (!isLocalHost(window.location.hostname)) return Promise.reject(err);
  return fetch(`/__dev-themes/${encodeURIComponent(id)}`)
    .then((res) => (res.ok ? res.text() : Promise.reject(err)))
    .then((definition) => {
      const def = JSON.parse(definition);
      return { id, nameAr: def.name && def.name.ar, nameEn: def.name && def.name.en, definition };
    });
}

export function resolveTenant() {
  if (tenantCache) return Promise.resolve(tenantCache);

  if (!tenantPromise) {
    tenantPromise = apiFetch("/lookup/tenant-by-host", { query: { host: currentHost() } })
      .then((tenant) => {
        const preview = previewRequest();
        if (!preview) {
          applyBranding(tenant);
          return tenant;
        }
        // the teacher's dashboard previewing another theme: display only, nothing is saved
        return apiFetch(`/lookup/themes/${preview.id}`)
          .catch((err) => devTheme(preview.id, err))
          .then((theme) => {
            applyBranding(tenant, { definition: theme.definition, choice: preview.choice });
            tenant.themePreview = theme;
            // the pages read the theme's blocks (home sections) from the branding too: show the previewed one's
            tenant.branding = Object.assign({}, tenant.branding, { themeDefinition: theme.definition });
            listenForDraft(theme.definition);
            return tenant;
          })
          .catch(() => {
            applyBranding(tenant);
            return tenant;
          });
      })
      .then((tenant) => {
        tenantCache = tenant;
        return tenant;
      })
      .catch((err) => {
        tenantPromise = null;
        throw err;
      });
  }

  return tenantPromise;
}

/** The tenant if it has already been resolved, else null. Pages seed their
 *  initial state with this so a second visit paints immediately instead of
 *  going through a null render first. */
export function cachedTenant() {
  return tenantCache;
}

/** Governorates/states are manasa-service's own (seeded independently of Hiilt's lookup_service
 *  -- see the extraction plan's Phase C), so these go through the normal apiFetch/api_path like
 *  everything else -- no separate gateway route to swap to. */
export function listGovernorates() {
  return apiFetch("/lookup/governorates");
}

export function listStates(governorateId) {
  return apiFetch("/lookup/states", { query: { governorateId } });
}

/** Student self-registration -- public, no auth required. */
export function registerStudent(payload) {
  return apiFetch("/students/register", { method: "POST", body: payload });
}

/** Where this student stopped in one item (seconds), or null -- each video keeps its own position. */
export function getItemProgress(courseId, itemId) {
  return apiFetch(`/courses/${courseId}/items/${itemId}/progress`).catch(() => null);
}

/** Public course catalog for a teacher -- browsable with no account. */
export function listCourseCatalog(teacherUserId) {
  return apiFetch("/courses/catalog", { query: { teacherUserId } });
}

/** Full curriculum for one course. Works with or without a logged-in session: an anonymous or
 *  non-enrolled visitor gets non-preview items locked (contentUrl withheld), same as the
 *  Flutter app's course detail screen. */
export function getCourse(id) {
  return apiFetch(`/courses/${id}`, { auth: true });
}

/** The coach's library (videos, or recipes with type=ARTICLE): search, filters, paging, facets. */
export function searchLibrary(params) {
  return apiFetch("/library", { query: params, auth: true });
}

export function libraryFavorites() {
  return apiFetch("/library/favorites", { auth: true });
}

export function libraryHistory() {
  return apiFetch("/library/history", { auth: true });
}

export function setFavorite(itemId, on) {
  return apiFetch(`/library/favorites/${itemId}`, { method: on ? "PUT" : "DELETE", auth: true });
}

/** This month's workout calendar entries for the member (month "2026-10"), and the entries of one day. */
export function calendarMonth(month) {
  return apiFetch("/calendar/month", { query: { month }, auth: true });
}

export function calendarToday(date) {
  return apiFetch("/calendar/today", { query: { date }, auth: true });
}

export function tickEntry(entryId, on) {
  return apiFetch(`/calendar/entries/${entryId}/done`, { method: on ? "PUT" : "DELETE", auth: true });
}

/** Public: the membership plans a coach offers (pricing block of the home page). */
export function listPlans(teacherUserId) {
  return apiFetch("/lookup/plans", { query: { teacherUserId } });
}

/** Public: the coach's trainers (team block of the home page). */
export function listInstructors(teacherUserId) {
  return apiFetch("/lookup/instructors", { query: { teacherUserId } });
}

/** STUDENT-only: this member's memberships (see PlanController). */
export function myPlans() {
  return apiFetch("/plans/mine", { auth: true });
}

/** STUDENT-only: asks for a plan, or starts its free trial. */
export function joinPlan(planId, trial) {
  return apiFetch(`/plans/${planId}/join`, { method: "POST", body: { trial: !!trial }, auth: true });
}

/** STUDENT-only: the teacher's upcoming / live sessions this student may attend (see MeetingController). */
export function listMyMeetings() {
  return apiFetch("/meetings/mine", { auth: true });
}

/** STUDENT-only: a connection to a live session (LiveKit url + token + watermark text). */
export function joinMeeting(id) {
  return apiFetch(`/meetings/${id}/join`, { method: "POST", auth: true });
}

/** The chat of a live session, kept on the server: everything said so far (oldest first), or with `after` only newer
 *  messages. Lets a student who joins late read what was said before. */
export function meetingChatHistory(id, after) {
  return apiFetch(`/meetings/${id}/chat`, { auth: true, query: { after } });
}

/** Stores one chat message (the server decides: chat locked, too fast, removed from the meeting). */
export function meetingChatSend(id, text, clientId) {
  return apiFetch(`/meetings/${id}/chat`, { method: "POST", auth: true, body: { text, clientId } });
}

/** STUDENT-only: the meeting's polls / quick questions, and answering one (MeetingPollService). */
export function meetingPolls(id) {
  return apiFetch(`/meetings/${id}/polls`, { auth: true });
}

/** STUDENT-only: the meeting page caught the watermark removed / hidden (MeetingController#tamper). */
export function meetingTamper(id, detail) {
  return apiFetch(`/meetings/${id}/tamper`, { method: "POST", auth: true, body: { detail } });
}

export function meetingVote(id, pollId, option) {
  return apiFetch(`/meetings/${id}/polls/${pollId}/vote`, { method: "POST", auth: true, body: { option } });
}

/** STUDENT-only: request activation/enrollment in the period a course belongs to -- pending
 *  the teacher's approval (see StudentController#createPeriodOrder). */
export function requestSubscription({ periodId, branchId }) {
  return apiFetch("/students/period-orders", { method: "POST", auth: true, body: { periodId, branchId } });
}

/** STUDENT-only: records the last item opened in this course (and, for VIDEO/AUDIO, how far into
 *  it) so re-entering the course resumes where they left off -- see CourseController#saveProgress.
 *  positionSeconds is optional (omit/null for non-media items). Silently ignored if not logged in
 *  as a student (no auth token) since apiFetch would 401/403 and the caller doesn't need to react. */
export function saveCourseProgress(courseId, itemId, positionSeconds) {
  if (!isLoggedIn()) return Promise.resolve();
  return apiFetch(`/courses/${courseId}/progress`, {
    method: "PUT",
    auth: true,
    body: { itemId, positionSeconds: positionSeconds ?? null },
  }).catch(() => {});
}

/** Student/teacher login (user-service, not gateway-routed under /api/manasa). */
export async function login(email, password) {
  const res = await fetch(properties.auth_path + "/login", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error((json.error && json.error.message) || "بيانات الدخول غير صحيحة");
  }
  return json.payload; // { token, userId, email, fullName, role }
}
